/**
 * Error Reporter - отправляет ошибки на сервер для логирования и алертов.
 * DIY решение для мониторинга ошибок в production.
 */

import { API_BASE_URL } from '../config';

// Очередь ошибок для batch-отправки
let errorQueue = [];
let flushTimeout = null;
const FLUSH_DELAY = 2000; // 2 секунды debounce

/**
 * Отправляет ошибку на сервер
 * @param {Object} errorData - данные об ошибке
 */
async function sendError(errorData) {
    const apiUrl = API_BASE_URL || import.meta.env.VITE_API_URL;

    if (!apiUrl) {
        console.warn('[ErrorReporter] API URL not configured, skipping error report');
        return;
    }

    try {
        await fetch(`${apiUrl}/errors`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                errors: errorData,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
                url: window.location.href,
                platform: 'agr-frontend',
                buildVersion: import.meta.env.VITE_BUILD_VERSION || 'unknown',
                telegramUser: getTelegramUser(),
            }),
            // Не ждем ответа, чтобы не блокировать UI
            keepalive: true,
        });
    } catch (e) {
        // Тихо игнорируем ошибки отправки (чтобы не вызвать рекурсию)
        console.warn('[ErrorReporter] Failed to send error:', e.message);
    }
}

/**
 * Получает данные Telegram пользователя (если доступны)
 */
function getTelegramUser() {
    try {
        const webApp = window.Telegram?.WebApp;
        if (webApp?.initDataUnsafe?.user) {
            const user = webApp.initDataUnsafe.user;
            return {
                id: user.id,
                username: user.username,
                firstName: user.first_name,
            };
        }
    } catch (e) {
        // Игнорируем
    }
    return null;
}

/**
 * Flush очереди ошибок на сервер
 */
function flushErrors() {
    if (errorQueue.length === 0) return;

    const errors = [...errorQueue];
    errorQueue = [];
    flushTimeout = null;

    sendError(errors);
}

/**
 * Добавляет ошибку в очередь для отправки
 * @param {Object} error - объект ошибки
 */
function queueError(error) {
    errorQueue.push(error);

    // Debounce: отправляем пачкой через 2 секунды
    if (flushTimeout) {
        clearTimeout(flushTimeout);
    }
    flushTimeout = setTimeout(flushErrors, FLUSH_DELAY);
}

/**
 * Обрабатывает JavaScript ошибку
 * @param {ErrorEvent} event - событие ошибки
 */
export function handleWindowError(event) {
    const errorData = {
        type: 'error',
        message: event.message || 'Unknown error',
        source: event.filename || 'unknown',
        lineno: event.lineno || 0,
        colno: event.colno || 0,
        stack: event.error?.stack || null,
        timestamp: new Date().toISOString(),
    };

    console.error('[ErrorReporter] Captured error:', errorData.message);
    queueError(errorData);
}

/**
 * Обрабатывает необработанные Promise rejection
 * @param {PromiseRejectionEvent} event - событие rejection
 */
export function handleUnhandledRejection(event) {
    const reason = event.reason;
    const errorData = {
        type: 'unhandledrejection',
        message: reason?.message || String(reason) || 'Unknown rejection',
        stack: reason?.stack || null,
        timestamp: new Date().toISOString(),
    };

    console.error('[ErrorReporter] Captured unhandled rejection:', errorData.message);
    queueError(errorData);
}

/**
 * Инициализирует глобальные обработчики ошибок
 */
export function initErrorReporter() {
    // Только в production или test режиме отправляем на сервер
    const isProd = import.meta.env.PROD || import.meta.env.MODE === 'test';

    if (!isProd) {
        console.log('[ErrorReporter] Disabled in development mode');
        return;
    }

    console.log('[ErrorReporter] Initializing error reporter...');

    // Глобальный обработчик JavaScript ошибок
    window.addEventListener('error', handleWindowError);

    // Обработчик необработанных Promise rejection
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Отправляем оставшиеся ошибки при закрытии страницы
    window.addEventListener('beforeunload', flushErrors);

    console.log('[ErrorReporter] Initialized successfully');
}

/**
 * Вручную логирует ошибку (для использования в catch блоках)
 * @param {Error|string} error - ошибка или сообщение
 * @param {Object} context - дополнительный контекст
 */
export function reportError(error, context = {}) {
    const errorData = {
        type: 'manual',
        message: error?.message || String(error),
        stack: error?.stack || null,
        context,
        timestamp: new Date().toISOString(),
    };

    console.error('[ErrorReporter] Manual report:', errorData.message);
    queueError(errorData);
}

export default {
    init: initErrorReporter,
    report: reportError,
};
